import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography } from '@mui/material';
import styles from "items/inventory_info/styles.module.scss"; // Import the same styles used by InventoryNumberCell
import { deduplicateSizes, getCanonicalSize, sortSizes } from '../manual-override/utility/SizeAliases';

const FastEntrySizes = forwardRef(({ 
  item,
  onUpdate
}, ref) => {
  const [sizesData, setSizesData] = useState([]);
  const [sizeQuantities, setSizeQuantities] = useState({});
  const [focusedInput, setFocusedInput] = useState(null);
  const [invalidInputs, setInvalidInputs] = useState(new Set());
  
  // Create refs for the input fields
  const inputRefs = useRef({});
  
  // Expose the focus method to parent components
  useImperativeHandle(ref, () => ({
    focus: () => {
      // Focus the first size input if available
      if (sizesData.length > 0) {
        const firstSize = sizesData[0];
        if (inputRefs.current[firstSize]) {
          inputRefs.current[firstSize].focus();
          setFocusedInput(firstSize);
        }
      }
    }
  }));
  
  // Default sizes to show when no master_size_list is available
  const defaultSizes = ["XS", "S", "M", "L", "XL", "2XL"];

  // Check if style and color are selected
  const hasStyleAndColor = !!(item?.style && item?.color);

  // Update sizes when item changes
  useEffect(() => {
    if (item) {
      let sizesToUse = [];
      
      // Check if master_size_list is an array with values
      if (Array.isArray(item.master_size_list) && item.master_size_list.length > 0) {
        sizesToUse = item.master_size_list;
      } 
      // If it's not an array but an object with numeric keys (like from the log)
      else if (item.master_size_list && typeof item.master_size_list === 'object') {
        // Extract values from the object if it has numeric keys
        const sizeValues = Object.entries(item.master_size_list)
          .filter(([key]) => !isNaN(parseInt(key)))
          .map(([_, value]) => value);
        
        if (sizeValues.length > 0) {
          sizesToUse = sizeValues;
        } else {
          // Use default sizes if no valid size values found
          sizesToUse = defaultSizes;
        }
      } else {
        // Use default sizes if no master_size_list
        sizesToUse = defaultSizes;
      }
      
      // First normalize each size to its canonical form
      const normalizedSizes = sizesToUse.map(size => getCanonicalSize(size));
      
      // Then deduplicate and sort the sizes
      const processedSizes = sortSizes(deduplicateSizes(normalizedSizes));
      setSizesData(processedSizes);
      
      // Initialize quantities from item if available
      if (item.sizes) {
        // Convert size keys to canonical form
        const normalizedQuantities = {};
        
        // Create a Set of valid sizes for quick lookup
        const validSizesSet = new Set(processedSizes.map(size => getCanonicalSize(size)));
        
        Object.entries(item.sizes).forEach(([size, quantity]) => {
          const canonicalSize = getCanonicalSize(size);
          // Only keep sizes that exist in the new size list
          if (canonicalSize && validSizesSet.has(canonicalSize)) {
            normalizedQuantities[canonicalSize] = quantity;
          }
        });
        
        setSizeQuantities(normalizedQuantities);
        
        // If we filtered out any sizes, update the parent component
        if (Object.keys(normalizedQuantities).length !== Object.keys(item.sizes).length) {
          if (onUpdate) {
            // Calculate the total quantity directly here
            const totalQuantity = Object.values(normalizedQuantities).reduce((sum, qty) => sum + qty, 0);
            
            // Update the parent component with the new quantities and total
            onUpdate({
              ...item,
              sizes: normalizedQuantities,
              totalQuantity
            });
          }
        }
      } else {
        setSizeQuantities({});
      }
    } else {
      // Use default sizes if no item
      setSizesData(defaultSizes);
      setSizeQuantities({});
    }
  }, [item, onUpdate]);

  const handleQuantityChange = (size, e) => {
    const value = e.target.value;
    
    // Only allow numbers
    if (value === '' || /^[0-9]+$/.test(value)) {
      const newQuantities = { ...sizeQuantities };
      
      if (value === '') {
        delete newQuantities[size];
      } else {
        newQuantities[size] = parseInt(value, 10);
      }
      
      setSizeQuantities(newQuantities);
      
      // Calculate the total quantity directly here
      const totalQuantity = Object.values(newQuantities).reduce((sum, qty) => sum + qty, 0);
      
      // Update the parent component with the new quantities and total
      if (onUpdate) {
        onUpdate({
          ...item,
          sizes: newQuantities,
          totalQuantity
        });
      }
      
      // Remove any validation errors for this size
      setInvalidInputs(prev => {
        const newSet = new Set(prev);
        newSet.delete(size);
        return newSet;
      });
    }
  };

  const handleFocus = (size) => {
    setFocusedInput(size);
  };

  const handleBlur = () => {
    setFocusedInput(null);
  };

  const getInputClass = (size) => {
    const isInvalid = invalidInputs.has(size);
    const isFocused = focusedInput === size;
    const hasValue = size in sizeQuantities && sizeQuantities[size] > 0;
    
    // Return just the base class name, we'll apply styles directly
    return styles.inventoryInput;
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center',
      marginBottom: '12px',
      width: '100%'
    }}>
      <Box sx={{ 
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap', // Enable wrapping
        justifyContent: 'center', // Center the items
        gap: '4px', // Reduced from 8px
        opacity: hasStyleAndColor ? 1 : 0.7
      }}>
        {sizesData.map(size => (
          <Box 
            key={size} 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              margin: '2px' // Reduced from 4px
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem', 
                marginBottom: '2px',
                color: hasStyleAndColor ? 'text.primary' : 'text.secondary'
              }}
            >
              {size}
            </Typography>
            <div className={styles.inventoryNumberContainer}>
              <div className="text-center">
                <input
                  ref={el => inputRefs.current[size] = el}
                  className={getInputClass(size)}
                  data-is-valid={!invalidInputs.has(size)}
                  type="tel"
                  value={sizeQuantities[size] || ''}
                  placeholder={size}
                  onChange={(e) => handleQuantityChange(size, e)}
                  onFocus={() => handleFocus(size)}
                  onBlur={handleBlur}
                  disabled={!hasStyleAndColor}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                      let newQuantity = parseInt(sizeQuantities[size] || 0);
                      if (e.key === 'ArrowUp') {
                        newQuantity++;
                      } else if (e.key === 'ArrowDown' && newQuantity > 0) {
                        newQuantity--;
                      }
                      e.preventDefault();
                      handleQuantityChange(size, { target: { value: newQuantity.toString() } });
                    }
                  }}
                  style={{ 
                    width: '40px', 
                    height: '24px',
                    textAlign: 'center',
                    fontSize: '0.75rem',
                    padding: '0 4px',
                    backgroundColor: hasStyleAndColor 
                      ? (invalidInputs.has(size) 
                          ? 'rgba(200, 0, 0, 0.1)'  // Error background
                          : (focusedInput === size 
                              ? 'white'  // Focused state
                              : (sizeQuantities[size] && sizeQuantities[size] > 0 
                                  ? 'rgba(0, 255, 0, 0.05)'  // More subtle green highlight
                                  : 'white'))) 
                      : '#e0e0e0',
                    cursor: hasStyleAndColor ? 'text' : 'not-allowed',
                    color: hasStyleAndColor ? 'inherit' : '#757575',
                    border: invalidInputs.has(size) 
                      ? '1px solid red' 
                      : (focusedInput === size 
                          ? '1px solid rgb(2, 129, 192)' 
                          : '1px solid #c8c8c8'),
                    outline: invalidInputs.has(size) 
                      ? '1px solid red' 
                      : (focusedInput === size 
                          ? '1px solid rgb(2, 129, 192)' 
                          : 'none'),
                    boxShadow: invalidInputs.has(size) 
                      ? '0 0 4px red' 
                      : 'none'
                  }}
                />
              </div>
            </div>
          </Box>
        ))}
      </Box>
    </Box>
  );
});

export default FastEntrySizes;